import React, { useMemo } from "react";
import { Col, Row } from "reactstrap";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { InputAdornment, Typography } from "@mui/material";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import {
  getOptionLabel,
  getOptionValue,
  gstOptions,
  occupancyType,
} from "common/enums";

import { useConsumersQuery } from "apis/User";
import { useApplicantTypeQuery } from "apis/ApplicantType";
import { CONSUMER } from "common/constant";

function ApplicantDetails({ formik, t }) {
  const {
    values: {
      applicationTypeId,
      id,
      consumerNumber,
      applicant: { isGSTRegistered, applicantTypeId },
      connectionLoad: { powerRequirementUnit },
      inProgressApplicationId,
      isAllApplicationsProcessCompleted,
      extraValues: { isPanNumber, isGstNumber },
      payableBillAmount,
    },
    setFieldValue,
  } = formik;

  const { roles } = useSelector((state) => state.Login);

  const { data: { data: { items: applicantType } = {} } = {} } =
    useApplicantTypeQuery();

  const { data: { data: consumersOptions } = {} } = useConsumersQuery({
    applicationType: applicationTypeId,
    utilityId: getOptionValue("utilityOptions", "Power"),
  });

  const isIndividualApplicantType = useMemo(
    () =>
      applicantType?.find(
        (application) => application.name.toLowerCase() === "individual"
      ).id === applicantTypeId,
    [applicantType, applicantTypeId]
  );

  const isDisable = useMemo(
    () =>
      (inProgressApplicationId !== id && !isAllApplicationsProcessCompleted) ||
      payableBillAmount > 0,
    [
      inProgressApplicationId,
      id,
      isAllApplicationsProcessCompleted,
      payableBillAmount,
    ]
  );
  return (
    <>
      <Row>
        {roles.includes(CONSUMER) && (
          <Col md={4} sm={6}>
            <SelectField
              label={t("Customer Number") + "*"}
              name="utilityConsumerId"
              formik={formik}
              options={consumersOptions || []}
              getOptionLabel={(option) => option.consumerNumber}
              getOptionValue={(option) => option.id}
            />
          </Col>
        )}
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Customer Name")}
            name="consumerName"
            formik={formik}
            placeholder="Enter Customer Name"
            disabled
          />
        </Col>
        <Typography className="mb-3" lineHeight="1" variant="h6">
          {t("Applicant Details")}:
        </Typography>
        <Row>
          <Col md={4} sm={6}>
            <SelectField
              label={t("Applicant Type")}
              name="applicant.applicantTypeId"
              formik={formik}
              options={applicantType || []}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              disabled
            />
          </Col>
          <Col md={4} sm={6}>
            <SelectField
              label={t("Occupancy Type")}
              name="applicant.occupancyTypeId"
              formik={formik}
              options={occupancyType || []}
              disabled
            />
          </Col>
          {!isPanNumber && (
            <Col md={4} sm={6}>
              <InputFieldText
                label={
                  isIndividualApplicantType
                    ? t("PAN Number")
                    : t("PAN Number") + "*"
                }
                name="applicant.panNumber"
                formik={formik}
                disabled={isDisable}
                onChange={(e) =>
                  setFieldValue(
                    "applicant.panNumber",
                    e.target.value.toUpperCase()
                  )
                }
                placeholder={
                  isIndividualApplicantType
                    ? t("PAN Number")
                    : t("PAN Number") + "*"
                }
              />
            </Col>
          )}
          {!isGstNumber && (
            <Col md={4} sm={6}>
              <SelectField
                label={t("GST") + "*"}
                name="applicant.isGSTRegistered"
                onChange={(_, { value }) => {
                  setFieldValue("applicant.isGSTRegistered", value);
                  if (!value) {
                    setFieldValue("applicant.gstNumber", "");
                  }
                }}
                formik={formik}
                disabled={isDisable}
                options={gstOptions}
              />
            </Col>
          )}
          {!isGstNumber && isGSTRegistered && (
            <Col md={4} sm={6}>
              <InputFieldText
                label={t("GST Number") + "*"}
                name="applicant.gstNumber"
                formik={formik}
                onChange={(e) =>
                  setFieldValue(
                    "applicant.gstNumber",
                    e.target.value.toUpperCase()
                  )
                }
                placeholder="GST Number"
                disabled={isDisable}
              />
            </Col>
          )}
        </Row>
        <Typography className="mb-3" lineHeight="1" variant="h6">
          {t("Connection Details")}:
        </Typography>
        <Row>
          <Col md={4} sm={6}>
            <InputFieldText
              label={t("Current Tariff Category")}
              name="tariffName"
              formik={formik}
              disabled
            />
          </Col>
          <Col md={4} sm={6}>
            <InputFieldText
              label={t("Total Power Requirement")}
              name="connectionLoad.powerRequirement"
              formik={formik}
              disabled
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {getOptionLabel("unitOptions", powerRequirementUnit)}
                  </InputAdornment>
                ),
              }}
            />
          </Col>
        </Row>
      </Row>
      {!!inProgressApplicationId &&
      inProgressApplicationId !== id &&
      !isAllApplicationsProcessCompleted &&
      !!consumerNumber ? (
        <Typography variant="body2" color="error">
          {t(
            "There are some incomplete applications associated with this consumer number. You are unable to fill the form."
          )}
        </Typography>
      ) : payableBillAmount > 0 ? (
        <Typography variant="body2" color="error">
          You have an unpaid amount of {payableBillAmount} RS. Please pay first
          to continue.
        </Typography>
      ) : null}
    </>
  );
}

ApplicantDetails.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};
export default withTranslation()(ApplicantDetails);
